// MA-61 slice 1 — pure helpers for teacher class creation + roster paste.
// No Supabase/IO here (see classApi.ts for the data-access layer) so these
// are trivially unit-testable per this repo's pure-core convention.

// Excludes visually-ambiguous characters (0/O, 1/I) so a teacher reading a
// join code aloud to a room of 6-year-olds — or a kid typing it back — never
// hits a character that looks like another one (SPEC-classroom-mode.md §2).
const JOIN_CODE_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
const JOIN_CODE_LENGTH = 6;

/** `randomFn` returns a float in [0, 1) — injectable for deterministic tests. */
export function generateJoinCode(randomFn: () => number = Math.random): string {
  let code = "";
  for (let i = 0; i < JOIN_CODE_LENGTH; i++) {
    code += JOIN_CODE_ALPHABET[Math.floor(randomFn() * JOIN_CODE_ALPHABET.length)];
  }
  return code;
}

// MA-238 ma241-redemption-route slice (join-code-format-guard) — /join/:code
// has no class/teacher lookup to validate against (see JoinRedemption.tsx's
// header for why), but it can still tell a well-formed code from garbage
// (mistyped URL, bad QR decode) using the SAME alphabet/length
// generateJoinCode() draws from, so a real generated code can never fail
// this check.
export function isValidJoinCode(code: string): boolean {
  if (code.length !== JOIN_CODE_LENGTH) return false;
  for (const char of code) {
    if (!JOIN_CODE_ALPHABET.includes(char)) return false;
  }
  return true;
}

// MA-238 ma241-redemption-route slice (join-code-case-normalize) —
// generateJoinCode() only ever draws from the uppercase alphabet, but a
// parent manually typing a real code back (rather than scanning the QR or
// tapping the printed card's link) can easily type it lowercase, and a
// copy-paste can carry leading/trailing whitespace. Neither should fail
// isValidJoinCode against a genuinely real code — normalize BEFORE
// validating, displaying, or storing so the shape check and the printed
// card agree. Uppercasing can never turn a really-invalid code valid: the
// excluded ambiguous characters (0/O/1/I) are unaffected by case.
export function normalizeJoinCode(code: string): string {
  return code.trim().toUpperCase();
}

/** MA-926 (D-TUX-3) — why a pasted name was left out of the preview. */
export type RosterSkipReason = "on-roster" | "repeated" | "too-long";

/** The longest stored display_name (MA-926 S2; 0061's UPDATE policy holds
 *  the same bound). */
export const ROSTER_NAME_MAX = 40;

export interface RosterPreview {
  /** The names that will be saved, in paste order. */
  names: string[];
  /** The pasted entries left out, with why (shown in the preview). `addAs`
   *  (D-1013-18): the entry's short form matches a roster name, but the
   *  teacher cannot tell whether it is the same child, so it is left out by
   *  default and may be added as this longer form instead. `key` identifies
   *  the entry for `addAnyway`. */
  skipped: { name: string; reason: RosterSkipReason; addAs?: string; key?: string }[];
}

const nameKey = (s: string) => s.replace(/\s+/g, " ").trim().toLowerCase();

interface Parsed {
  entry: string;
  first: string;
  /** The second word as typed (MA-997 compares it with the offered form). */
  second: string;
  rest: string;
  /** Letters of `rest` the teacher typed as an abbreviation ("Ava Mo." keeps
   *  "Mo"); otherwise 1 (the initial). */
  min: number;
  key: string;
}

function parseEntry(raw: string): Parsed | null {
  const entry = raw.replace(/\t/g, " ").replace(/\s+/g, " ").trim();
  if (!entry) return null;
  const words = entry.split(" ");
  const first = words[0];
  const second = words[1] ?? "";
  const rest = second.replace(/[^\p{L}]/gu, "");
  const min = rest && second.endsWith(".") ? rest.length : 1;
  return { entry, first, second, rest, min, key: nameKey(rest ? `${first} ${rest}` : first) };
}

function shortName(first: string, rest: string, letters: number): string {
  if (!rest) return first;
  const part = rest.slice(0, Math.max(1, letters));
  return `${first} ${part[0].toUpperCase()}${part.slice(1).toLowerCase()}.`;
}

/** MA-997 (D-1017-4) — the longer form the paste and Rename offer: the first
 *  word starts with a capital letter ("ava morales" is offered "Ava Mo."), the
 *  second part is shortName's. */
function offeredName(p: Parsed, letters: number): string {
  const first = p.first.charAt(0).toUpperCase() + p.first.slice(1);
  return shortName(first, p.rest, letters);
}

/** MA-997 (D-1017-4, D-1017-15; Rename only) — the teacher typed the offered
 *  form without its final stop: the typed second word, AS TYPED (punctuation
 *  included, so "O'B" is not "Ob"), equals the offered second word less its
 *  ".", case ignored ("Ava Mo" / "ava mo" beside "Ava M." read as "Ava Mo."). */
function typedOfferedLetters(p: Parsed, offered: string): boolean {
  const offeredSecond = offered.slice(offered.lastIndexOf(" ") + 1).replace(/\.$/, "");
  return p.second.toLowerCase() === offeredSecond.toLowerCase();
}

/** The fewest letters (>= min) that make the name differ from every name in
 *  `taken`; null when even the whole second word does not. */
function lettersToDiffer(p: Parsed, from: number, taken: Set<string>): number | null {
  for (let n = Math.max(from, p.min); n <= Math.max(p.rest.length, 1); n++) {
    if (!taken.has(nameKey(shortName(p.first, p.rest, n)))) return n;
  }
  return null;
}

/**
 * MA-926 (D-TUX-3, D-1013-18) — the add box's rules, pure:
 *  - entries are split on new lines AND commas; a tab inside a line is a
 *    space, so a spreadsheet's First and Last cells stay one child;
 *  - each entry keeps its first word plus the initial of its second word
 *    ("Ava Martinez" -> "Ava M."; the schema allows first name or initial
 *    only, 0023:58), or the letters the teacher typed as an abbreviation
 *    ("Ava Mo." stays); a third word and later are dropped;
 *  - when two pasted children would read the same, letters of the second
 *    word are added until they differ ("Ava Ma." and "Ava Mo.");
 *  - an entry repeated in the paste (case-insensitive, first wins), or longer
 *    than ROSTER_NAME_MAX once shortened, is left out;
 *  - an entry whose typed form or short form is already a roster name is
 *    left out ("on-roster"); when only the short form matches, the teacher
 *    cannot know whether it is the same child, so the skip carries `addAs`,
 *    the form with the letters needed to differ from every roster name, and
 *    listing its `key` in `addAnyway` adds it as that form;
 *  - MA-997 (D-1017-4, D-1017-15): that offered form starts each word with a
 *    capital letter, it differs from every roster name AND every name this
 *    paste already adds or offers (paste order), and a chosen entry is saved
 *    as exactly the offered form, so the button never saves a name the
 *    preview did not show. The paste never reads a typed second word as the
 *    offered form (Rename does): a re-pasted class list adds nothing.
 */
export function previewRosterNames(
  text: string,
  existingNames: readonly string[] = [],
  addAnyway: readonly string[] = [],
): RosterPreview {
  type Skip = RosterPreview["skipped"][number];
  type Kept = { p: Parsed; letters: number; at: number };
  type Clash = { p: Parsed; at: number; slot: Skip };
  const onRoster = new Set(existingNames.map(nameKey));
  const anyway = new Set(addAnyway);
  const skipped: (Skip | null)[] = [];
  const seen = new Set<string>();
  const kept: Kept[] = [];
  const clashes: Clash[] = [];
  let at = 0;
  for (const raw of text.split(/\r?\n|,/)) {
    const p = parseEntry(raw);
    if (!p) continue;
    at++;
    if (seen.has(p.key)) {
      skipped.push({ name: p.entry, reason: "repeated" });
      continue;
    }
    seen.add(p.key);
    if (onRoster.has(nameKey(p.entry))) {
      skipped.push({ name: p.entry, reason: "on-roster" });
      continue;
    }
    if (onRoster.has(nameKey(shortName(p.first, p.rest, p.min)))) {
      // Its slot keeps paste order among the left-out lines; the offer is
      // filled in once this paste's own names are known.
      const slot: Skip = { name: p.entry, reason: "on-roster" };
      skipped.push(slot);
      clashes.push({ p, at, slot });
      continue;
    }
    kept.push({ p, letters: p.min, at });
  }
  // Pasted children who would read the same: the fewest letters that tell
  // every one of them apart (never fewer than each one's own).
  const groups = new Map<string, Kept[]>();
  for (const k of kept) {
    const g = nameKey(shortName(k.p.first, k.p.rest, k.letters));
    groups.set(g, [...(groups.get(g) ?? []), k]);
  }
  for (const members of groups.values()) {
    if (members.length < 2) continue;
    const longest = Math.max(...members.map((m) => m.p.rest.length), 1);
    let n = Math.max(...members.map((m) => m.letters));
    while (
      n < longest &&
      new Set(members.map((m) => m.p.rest.slice(0, Math.max(n, m.letters)).toLowerCase())).size < members.length
    ) {
      n++;
    }
    for (const m of members) m.letters = Math.max(n, m.letters);
  }
  const added: { name: string; at: number }[] = [];
  for (const k of kept) {
    const name = shortName(k.p.first, k.p.rest, k.letters);
    if (onRoster.has(nameKey(name))) {
      skipped.push({ name: k.p.entry, reason: "on-roster" });
      continue;
    }
    if (name.length > ROSTER_NAME_MAX) {
      skipped.push({ name: k.p.entry, reason: "too-long" });
      continue;
    }
    added.push({ name, at: k.at });
  }
  // The offers (MA-997 S2): against the roster, this paste's names, and the
  // offers before it, so no two buttons or names share a form.
  const taken = new Set([...onRoster, ...added.map((a) => nameKey(a.name))]);
  for (const c of clashes) {
    const n = c.p.rest ? lettersToDiffer(c.p, c.p.min + 1, taken) : null;
    const addAs = n === null ? undefined : offeredName(c.p, n);
    if (!addAs || addAs.length > ROSTER_NAME_MAX) continue;
    taken.add(nameKey(addAs));
    if (anyway.has(c.p.key)) {
      skipped[skipped.indexOf(c.slot)] = null;
      added.push({ name: addAs, at: c.at });
    } else {
      Object.assign(c.slot, { addAs, key: c.p.key });
    }
  }
  added.sort((a, b) => a.at - b.at);
  return { names: added.map((a) => a.name), skipped: skipped.filter((s): s is Skip => s !== null) };
}

/**
 * The names a paste saves (MA-926 rules, see `previewRosterNames`). Pure — no
 * roster-size cap here; the caller enforces any product-level limit.
 */
export function parseRosterNames(
  text: string,
  existingNames: readonly string[] = [],
  addAnyway: readonly string[] = [],
): string[] {
  return previewRosterNames(text, existingNames, addAnyway).names;
}

/** MA-926 (D-1013-18) — Rename's rule: one name, shortened like the add box
 *  (typed abbreviation letters kept: "Ava Mo." stays), at most
 *  ROSTER_NAME_MAX characters. MA-981 (D-1013-21, D-1013-33): the clash test
 *  IS the paste — the one typed name is run through `previewRosterNames`
 *  against the other roster names, so case and spacing are ignored exactly as
 *  the paste ignores them, and a name the paste would leave out "already on
 *  the roster" is a clash. When the paste would offer "Add as <longer form>",
 *  the clash carries it as `tryAs` (only when it fits ROSTER_NAME_MAX, as the
 *  paste only adds one that fits). MA-997 (D-1017-4, D-1017-15; Rename only):
 *  a second word typed as that offered form without its stop is saved as the
 *  offered form ("Ava Mo" beside "Ava M." saves "Ava Mo."), and a typed
 *  abbreviation whose initial form clashes is saved in the same casing
 *  ("ava mo." beside "Ava M." saves "Ava Mo."). */
export function renameDisplayName(
  text: string,
  otherNames: readonly string[],
): { name: string } | { error: "empty" | "one-name" | "too-long" | "clash"; tryAs?: string } {
  if (/[,\n\r]/.test(text)) return { error: "one-name" };
  const p = parseEntry(text);
  if (!p) return { error: "empty" };
  const name = shortName(p.first, p.rest, p.min);
  if (name.length > ROSTER_NAME_MAX) return { error: "too-long" };
  const preview = previewRosterNames(text, otherNames);
  const left = preview.skipped.find((s) => s.reason === "on-roster");
  if (left) {
    const offer = left.addAs && left.addAs.length <= ROSTER_NAME_MAX ? left.addAs : undefined;
    if (offer && typedOfferedLetters(p, offer)) return { name: offer };
    return offer ? { error: "clash", tryAs: offer } : { error: "clash" };
  }
  const others = new Set(otherNames.map(nameKey));
  if (p.min > 1 && others.has(nameKey(shortName(p.first, p.rest, 1)))) return { name: offeredName(p, p.min) };
  return { name };
}
